import React, { useEffect, useState } from "react";
import { resolveImageGeneratorResultSrc } from "../../app/api/imageGenerator";
import { FieldSelect, helperTextClassName } from "./ImageGeneratorControls";
import { reportSlidePageButtonClassName } from "./collectionBuildStyles";
import { ImageGeneratorReportSlide } from "./ImageGeneratorReportSlides";

/** Read-only report carousel for View report (macro / colour / micro). */
const ImageGeneratorReportSlider: React.FC<{
  slides: ImageGeneratorReportSlide[];
  emptyMessage?: string;
}> = ({ slides, emptyMessage = "No report slides for this selection." }) => {
  const total = slides.length;
  const [page, setPage] = useState(1);
  const slidesKey = slides.map(slide => slide.id).join("|");

  useEffect(() => {
    setPage(1);
  }, [slidesKey]);

  useEffect(() => {
    setPage(prev => (total > 0 ? Math.min(Math.max(prev, 1), total) : 1));
  }, [total]);

  const safePage = total > 0 ? Math.min(Math.max(page, 1), total) : 1;
  const current = total > 0 ? slides[safePage - 1] : null;
  const rawUrl = current?.url || "";
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    setSrc(null);
    if (!rawUrl) {
      return;
    }
    (async () => {
      try {
        const preview = await resolveImageGeneratorResultSrc(rawUrl);
        if (cancelled) {
          if (preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
          }
          return;
        }
        if (preview.startsWith("blob:")) {
          blobUrl = preview;
        }
        setSrc(preview);
      } catch {
        if (!cancelled) {
          setSrc(rawUrl);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [rawUrl]);

  if (total === 0) {
    return <p className={`${helperTextClassName} mt-3`}>{emptyMessage}</p>;
  }

  return (
    <div className="mt-4 w-full max-w-full text-left">
      <div className="inline-block max-w-full">
        <div className="relative inline-block max-w-full overflow-hidden rounded-[20px] border border-[#737373]/50 bg-white p-0 leading-none">
          {src ? (
            <img
              src={src}
              alt={current?.name || "Report slide"}
              className="block h-auto w-auto max-h-[460px] max-w-full object-contain sm:max-h-[560px]"
              draggable={false}
            />
          ) : rawUrl ? (
            <span className="flex h-[240px] w-[min(100%,640px)] items-center justify-center bg-[#F3F3F3] px-4 text-sm text-tertiary sm:h-[300px]">
              Loading…
            </span>
          ) : (
            <span className="flex h-[240px] w-[min(100%,640px)] items-center justify-center bg-[#F3F3F3] px-4 text-sm text-secondary sm:h-[300px]">
              {current?.name || "Slide"}
            </span>
          )}
        </div>

        <div className="mt-3 flex items-center justify-end gap-2 text-sm text-secondary">
          <button
            type="button"
            aria-label="Previous report slide"
            disabled={safePage <= 1}
            onClick={() => setPage(Math.max(1, safePage - 1))}
            className={reportSlidePageButtonClassName}
          >
            &lt;
          </button>
          <div className="w-[72px]">
            <FieldSelect
              value={String(safePage)}
              onChange={next => setPage(Number(next))}
              options={Array.from({ length: total }, (_, index) => ({
                value: String(index + 1),
                label: String(index + 1),
              }))}
            />
          </div>
          <span>of {total}</span>
          <button
            type="button"
            aria-label="Next report slide"
            disabled={safePage >= total}
            onClick={() => setPage(Math.min(total, safePage + 1))}
            className={reportSlidePageButtonClassName}
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageGeneratorReportSlider;
